import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Alert, Form } from 'react-bootstrap';
import { getRoles } from '../services/api/roleApi';
import { getUserRoles, assignRoleToUser, removeRoleFromUser } from '../services/api/userApi';

const UserRoles = ({ user, onBack }) => {
  const [userRoles, setUserRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRole, setSelectedRole] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [userRolesResponse, allRolesResponse] = await Promise.all([
        getUserRoles(user.id),
        getRoles()
      ]);
      
      setUserRoles(userRolesResponse.data);
      setAvailableRoles(allRolesResponse.data);
    } catch (error) {
      setError('Failed to fetch roles data');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchData();
  }, [user, fetchData]);

  const handleAddRole = async () => {
    if (!selectedRole) return;
    
    try {
      await assignRoleToUser(user.id, selectedRole);
      fetchData();
      setSelectedRole('');
    } catch (error) {
      setError('Failed to assign role to user');
    }
  };

  const handleRemoveRole = async (roleId) => {
    try {
      await removeRoleFromUser(user.id, roleId);
      fetchData();
    } catch (error) {
      setError('Failed to remove role from user');
    }
  };

  const getUnassignedRoles = () => {
    const assignedIds = new Set(userRoles.map(r => r.id));
    return availableRoles.filter(r => !assignedIds.has(r.id));
  };

  if (loading) return <div>Loading roles...</div>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Manage Roles for {user.firstName} {user.lastName}</h2>
        <Button variant="secondary" onClick={onBack}>
          Back to Users
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-4">
        <Card.Header>Add Role</Card.Header>
        <Card.Body>
          <div className="d-flex">
            <Form.Select 
              value={selectedRole} 
              onChange={(e) => setSelectedRole(e.target.value)}
              className="me-2"
            >
              <option value="">Select a role</option>
              {getUnassignedRoles().map(role => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </Form.Select>
            <Button 
              variant="primary" 
              onClick={handleAddRole}
              disabled={!selectedRole}
            >
              Add Role
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>Current Roles</Card.Header>
        <Card.Body>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {userRoles.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center">No roles assigned to this user</td>
                </tr>
              ) : (
                userRoles.map(role => (
                  <tr key={role.id}>
                    <td>{role.name}</td>
                    <td>{role.description}</td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveRole(role.id)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default UserRoles;